import { Center } from "@chakra-ui/react";
import { isQueryApiError } from "@features/errors/libs/guards";
import {
  type ErrorComponentProps,
  SearchParamError,
  useRouter,
} from "@tanstack/react-router";
import { ZodError } from "zod";

import { SearchParamErrorPage } from "@pages/error/ui/search-param-error-page.tsx";
import { Icon } from "@shared/ui/chakra";
import { EmptyState } from "@shared/ui/templates/empty-state";

import { useErrorPageMessages } from "./hooks/use-error-page-messages";

export const ErrorPageConnector = <T,>({
  error,
  reset,
}: ErrorComponentProps<T>) => {
  const router = useRouter();
  const { queryClient } = router.options.context;
  const texts = useErrorPageMessages();

  const handleRetry = async () => {
    await queryClient.refetchQueries();
    await router.invalidate();
    reset();
    window.location.reload();
  };

  const handleGoHome = async () => {
    await router.navigate({ to: "/" });
  };

  if (isQueryApiError(error)) {
    if (error.group === "NOT_FOUND_ERROR") {
      return (
        <Center flexGrow={1}>
          <EmptyState
            buttonText={texts.notFound.buttonText}
            iconSlot={
              <Icon color="fg.subtle" name="File" size="2xl" variant="frame" />
            }
            subtitle={texts.notFound.subtitle}
            title={texts.notFound.title}
            onAction={handleGoHome}
          />
        </Center>
      );
    }

    if (error.group === "UNEXPECTED_ERROR") {
      return (
        <Center flexGrow={1}>
          <EmptyState
            buttonText={texts.unexpected.buttonText}
            iconSlot={<Icon color="fg.warning" name="Alert" size="2xl" />}
            subtitle={texts.unexpected.subtitle}
            title={texts.unexpected.title}
            onAction={handleRetry}
          />
        </Center>
      );
    }
  }

  if (error instanceof SearchParamError) {
    if (error.cause instanceof ZodError) {
      return (
        <Center flexGrow={1}>
          {" "}
          <SearchParamErrorPage error={error.cause} onGoBack={reset} />{" "}
        </Center>
      );
    }
  }

  return (
    <Center flexGrow={1}>
      <EmptyState
        buttonText={texts.common.buttonText}
        iconSlot={<Icon color="fg.warning" name="Alert" />}
        subtitle={texts.common.subtitle}
        title={texts.common.title}
        onAction={handleRetry}
      />
    </Center>
  );
};
